<template>
  <div class="cost-analysis-container modern-scrollbar" :class="{ 'is-embedded': embedded }">

    <!-- ── Header (full page only) ── -->
    <div v-if="!embedded" class="py-4 container-fluid">
      <div class="section-header-card card">
        <div class="card-body p-0">
          <div class="row gx-4 align-items-center">
            <div class="col-auto my-auto">
              <h5 class="mb-0 header-title">{{ $t("Files.costServiceTitle") }}</h5>
              <p class="mb-0 text-xs text-muted">{{ $t("Files.secondaryTitle") }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Subscribed services ── -->
    <div :class="embedded ? '' : 'container-fluid'">
      <div v-if="inProgress" class="col-12 mt-1 loading-state">
        <div class="custom-loader">
          <div class="loader-circle"/>
          <div class="loader-circle"/>
          <div class="loader-circle"/>
          <div class="loader-circle"/>
        </div>
        <p class="loading-text">Loading...</p>
      </div>

      <!-- ── Embedded mode: original compact layout ── -->
      <div v-else-if="embedded" class="services-grid modern-scrollbar">
        <div v-for="nth in COST_SERVICES || []" :key="nth.subscriptionName" class="service-card">
          <div class="service-header-embedded">
            <div class="service-image">
              <img :src="resolvePublicUrl(nth.thumbnail)" alt="service thumbnail" />
            </div>
            <div class="service-info">
              <div class="service-title-row">
                <h6 class="service-title">{{ nth.subscriptionName }}</h6>
              </div>
              <div class="service-actions">
                <span
                    v-if="getLatestFile(nth)"
                    class="latest-file-link"
                    @click="openLatestFile(getLatestFile(nth),nth.subscriptionName)"
                >
                  <span class="latest-file-name">{{ getLatestFile(nth).file.fileName }}</span>
                  <span class="latest-file-date">Updated {{ formatDate(getLatestFile(nth).file.startDate) }}</span>
                </span>
                <span v-else class="no-files-label">No files available</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Full page mode: Files-style accordion layout ── -->
      <div v-else class="accordion-list">
        <div
            v-for="service in COST_SERVICES || []"
            :key="service.id || service.subscriptionName"
            class="accordion-service-card"
        >
          <!-- Service header row -->
          <div class="service-header">
            <div class="service-thumbnail">
              <img :src="resolvePublicUrl(service.thumbnail)" alt="" class="thumb-img" />
            </div>

            <div class="service-meta">
              <div class="service-identity">
                <span class="service-name">{{ service.subscriptionName }}</span>
              </div>

              <!-- Flat folder list -->
              <div class="folder-flat-list">
                <div v-for="folder in visibleFolders(service.folders)" :key="folder.folderName" class="folder-wrap">
                  <div
                      class="folder-row"
                      :class="{ 'folder-row--active': isOpen(service, folder), 'folder-row--disabled': folderIsDisabled(folder) }"
                      @click="toggleFolder(service, folder, service.subscriptionName)"
                  >
                    <span class="folder-name">{{ folder.folderName }}</span>
                    <span class="folder-meta">
                        <span v-if="folder.isArchived" class="updated-date">{{ $t("Files.archived") }}</span>
                        <span v-else-if="latestDate(folder)" class="updated-date">Updated {{ latestDate(folder) }}</span>
                        <span v-if="folder.redirectUrl" class="folder-badge folder-badge--link"><i class="fa fa-external-link"></i></span>
                        <i v-else-if="folder.hasFiles" class="chevron-icon fa" :class="isOpen(service, folder) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </span>
                  </div>

                  <!-- inline file panel -->
                  <div v-if="isOpen(service, folder)" class="inline-file-panel">
                    <div class="inline-file-inner">
                      <div v-for="file in openFolder.files" :key="file.id || file.title" class="file-row">
                        <div class="file-info">
                          <span class="file-title">{{ file.title }}</span>
                          <span v-if="file.isArchived" class="updated-date">{{ $t("Files.archived") }}</span>
                          <span v-else class="file-too-large-note">File too large to view or email</span>
                        </div>
                        <div v-if="!file.isArchived" class="file-actions">
                          <button class="action-btn action-btn--download" title="Download" @click="downloadFile(file)"><i class="fa fa-download"></i></button>
                        </div>
                      </div>
                      <div v-if="!openFolder.files || openFolder.files.length === 0" class="no-files">No files available.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Unsubscribed section (full page only) ── -->
    <template v-if="!embedded && UNSUBSCRIBED_COST_SERVICES?.length">
      <div class="py-4 container-fluid">
        <div class="section-header-card card">
          <div class="card-body p-0">
            <div class="row gx-4 align-items-center">
              <div class="col-auto my-auto">
                <h5 class="mb-0 header-title">Other Cost Analysis</h5>
                <p class="mb-0 text-xs text-muted"><a href="mailto:info@projectblue.com">Contact the team</a> to learn more about our other product offerings.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="container-fluid">
        <div class="accordion-list accordion-list--unsubscribed">
          <div
              v-for="service in UNSUBSCRIBED_COST_SERVICES"
              :key="service.id || service.subscriptionName"
              class="accordion-service-card accordion-service-card--locked"
          >
            <div class="service-header">
              <div class="service-thumbnail">
                <img :src="resolvePublicUrl(service.thumbnail)" alt="" class="thumb-img" />
                <div class="thumb-lock-overlay"><i class="fa fa-lock"></i></div>
              </div>

              <div class="service-meta">
                <div class="service-identity">
                  <span class="service-name">{{ service.subscriptionName }}</span>
                </div>

                <div class="folder-flat-list">
                  <div v-for="folder in visibleFolders(service.folders)" :key="folder.folderName" class="folder-wrap">
                    <div
                        class="folder-row"
                        :class="{ 'folder-row--active': isOpen(service, folder), 'folder-row--disabled': folderIsDisabled(folder) }"
                        @click="toggleFolder(service, folder)"
                    >
                      <span class="folder-name">{{ folder.folderName }}</span>
                      <span class="folder-meta">
                        <span v-if="folder.isArchived" class="updated-date">{{ $t("Files.archived") }}</span>
                        <span v-else-if="latestDate(folder)" class="updated-date">Updated {{ latestDate(folder) }}</span>
                        <span v-if="folder.redirectUrl" class="folder-badge folder-badge--link"><i class="fa fa-external-link"></i></span>
                        <i v-else-if="folder.hasFiles" class="chevron-icon fa" :class="isOpen(service, folder) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </span>
                    </div>

                    <!-- inline file panel -->
                    <transition name="expand">
                      <div v-if="isOpen(service, folder)" class="inline-file-panel">
                        <div class="inline-file-inner">
                          <div v-for="file in openFolder.files" :key="file.id || file.title" class="file-row">
                            <div class="file-info">
                              <span class="file-title">{{ file.title }}</span>
                              <span v-if="file.isArchived" class="updated-date">{{ $t("Files.archived") }}</span>
                              <span v-else class="file-too-large-note">File too large to view or email</span>
                            </div>
                            <div v-if="!file.isArchived" class="file-actions">
                              <button class="action-btn action-btn--download" title="Download" @click="downloadFile(file)"><i class="fa fa-download"></i></button>
                            </div>
                          </div>
                          <div v-if="!openFolder.files || openFolder.files.length === 0" class="no-files">No files available.</div>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ── Drawer (embedded mode still uses it) ── -->
    <NDrawer v-model:show="showDrawer" @update:show="clearProp()" :width="502" placement="right">
      <NDrawerContent>
        <div class="drawer-content">
          <div class="drawer-header">
            <h6 v-if="showCritMatList">{{ $t(`Files.seriesNames.CRITICAL_MATERIALS`) }}</h6>
            <h6 v-if="showMarketSupportForm">{{ $t(`Files.seriesNames.MARKET_SUPPORT`) }}</h6>
            <h6 v-if="showFiles">{{ folderName }}</h6>
          </div>
          <div class="drawer-body">
            <NDataTable v-if="showCritMatList" :columns="columns" :data="criticalMaterialsData" />
            <MarketSupportForm
                v-if="showMarketSupportForm"
                :elementProp="elementArrayProp"
                :userEmail="userEmail"
                :product="product"
                @customEvent="handleCustomEvent"
            />
            <NDataTable v-if="showFiles" :columns="columns" :data="data" />
          </div>
        </div>
      </NDrawerContent>
    </NDrawer>
  </div>
</template>

<script setup>
import { sortBy, forEach } from "lodash";
import jwtDecode from "jwt-decode";
import { onMounted, ref, computed, h, onBeforeMount } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  NDrawer,
  NDrawerContent,
  NDataTable
} from "naive-ui";
import { ROUTES } from "../../routes/names";
import {
  USER_FILES,
  getUserFiles,
  MARKET_SERVICES2,
  COST_SERVICES,
  MONTHLY_SERVICES,
  CRITICAL_MATERIALS_NAME,
  inProgress,
  filesRequestPercentage,
  changeColor,
  DATA_SERIES_TYPE,
  UNSUBSCRIBED_COST_SERVICES,
  downloadFile as downloadFileComposable,
  sendFileToEmail,
  visibleFolders,
  folderIsDisabled,
} from "../../composables/files/list";
import TableCell from "../../components/FilesTable/TableCell";
import TableCellDownload from "../../components/FilesTable/TableCellDownload";
import TableHeader from "../../components/FilesTable/TableHeader";
import { useI18n } from "vue-i18n";
import MarketSupportForm from "../../components/Drawer/MarketSupport";
import { saveToUserLogs, userLogInputFields } from "../../composables/userLogs";
import { resolvePublicUrl } from "../../utils/fileUrl";
const props = defineProps({
  embedded: {
    type: Boolean,
    default: false
  }
});

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// ── State ──────────────────────────────────────────────
const openFolder = ref(null); // { serviceId, folderName, files[] }

// Drawer state (kept for embedded mode / legacy)
const showDrawer = ref(false);
const showCritMatList = ref(false);
const showMarketSupportForm = ref(false);
const showFiles = ref(false);
const selectedSeries = ref(null);
let selectedElement = ref(null);
let product = ref(null);
const emailingFileId = ref(null);
let folderName = ref(null);
let elementArrayProp = ref([]);
const FOLDERS = ref([]);
const criticalMaterialsData = ref([]);
const sortedFolders = ref([]);
const isMonthlyService = ref(null);

// ── Helpers ────────────────────────────────────────────
const serviceKey = (service) => service.id || service.subscriptionName;

const isOpen = (service, folder, subscriptionName = null) =>
    openFolder.value?.serviceId === serviceKey(service) &&
    openFolder.value?.folderName === folder.folderName;

const toggleFolder = (service, folder, subscriptionName = null) => {
  if (folder?.isArchived) return;
  if (folder.redirectUrl) {
    window.location.href = folder.redirectUrl;
    return;
  }
  if (!folder.hasFiles) return;

  if (isOpen(service, folder)) {
    openFolder.value = null;
    return;
  }
  selectedElement.value = subscriptionName;
  folderName.value = folder.folderName;
  let fileData = folder.files;
  const files = (fileData || []).filter((f) => !f.isHidden).map((f) => ({
    title: f.fileName,
    type: f.fileType,
    url: f.fileUrl,
    id: f.fileUuid,
    startDate: f.startDate || null,
    isArchived: !!f.isArchived,
  }));

  openFolder.value = {
    serviceId: serviceKey(service),
    folderName: folder.folderName,
    files,
  };
};
const viewFile = async (file) => {
  console.log("viewFile", file);
  const fileExtension = file.url?.split('.').pop().split('?')[0].toLowerCase();
  if (fileExtension === "xls" || fileExtension === "xlsx") {
    window.open(`https://docs.google.com/viewer?embedded=true&url=${file.url}`);
    return;
  }

  // Open the file immediately — don't let logging block or break it
  window.open(file.url);

  // Log in the background, silently catch errors
  try {
    userLogInputFields.value = {
      userEmail: localStorage.getItem('email'),
      description: `User ${localStorage.getItem('email')} viewed ${file.url}.`,
      action: "ViewFile",
      method: null,
      statusCode: 200,
      environment: window.location.hostname,
      endpoint: null,
      path: null,
    };
    await saveToUserLogs(userLogInputFields.value);
  } catch (e) {
    console.warn("User log failed:", e);
  }
};

const latestDate = (folder) => {
  if (folder.startDate) {
    const d = new Date(folder.startDate);
    if (!isNaN(d)) return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  }
  if (!folder.files || folder.files.length === 0) return null;
  const dates = folder.files
      .map((f) => f.startDate)
      .filter(Boolean)
      .map((d) => new Date(d))
      .filter((d) => !isNaN(d));
  if (!dates.length) return null;
  const latest = new Date(Math.max(...dates));
  return latest.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const HISTORICAL_KEYWORDS = ["background", "analysis", "trade", "producer", "history", "cost tracker"];
const FORECAST_KEYWORDS = ["short-term", "medium-term", "long-term", "outlook", "forecast"];

const folderCategory = (folderName) => {
  const lower = folderName.toLowerCase();
  if (HISTORICAL_KEYWORDS.some((k) => lower.includes(k))) return "HISTORICAL ANALYSIS";
  if (FORECAST_KEYWORDS.some((k) => lower.includes(k))) return "FORECASTS";
  return "DATA";
};

const groupedFolders = (folders = []) => {
  const groups = {
    "HISTORICAL ANALYSIS": [],
    FORECASTS: [],
    DATA: [],
  };
  (folders || []).forEach((f) => groups[folderCategory(f.folderName)].push(f));
  return Object.entries(groups).map(([label, flds]) => ({ label, folders: flds }));
};

const fileIcon = (type) => {
  if (!type) return "fa fa-file";
  const t = type.toLowerCase();
  if (t === "pdf") return "fa fa-file-pdf-o";
  if (["xls", "xlsx"].includes(t)) return "fa fa-file-excel-o";
  return "fa fa-file-o";
};

const formatFileDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// ── Actions ────────────────────────────────────────────
const viewFileInline = async (file) => {
  const ext = file.url?.split('.').pop().split('?')[0].toLowerCase();
  if (ext === "xls" || ext === "xlsx") {
    window.open(`https://docs.google.com/viewer?embedded=true&url=${file.url}`);
    return;
  }
  userLogInputFields.value = {
    userEmail: localStorage.getItem('email'),
    description: `User ${localStorage.getItem('email')} viewed ${file.url}.`,
    action: "ViewFile",
    method: null,
    statusCode: 200,
    environment: window.location.hostname,
    endpoint: null,
    path: null,
  };
  await saveToUserLogs(userLogInputFields.value);
  window.open(file.url);
};

const sendToEmail = async (file) => {
  emailingFileId.value = file.id;
  try {
    await sendFileToEmail(file.id);
  } finally {
    emailingFileId.value = null;
  }
};

const downloadFile = async (file) => {
  await downloadFileComposable(file.id, selectedElement.value, openFolder.value?.folderName);
};

// ── Drawer / legacy helpers (embedded + drawer flows) ──
const viewFiles = (folder, isSubscribed, subscriptionName) => {
  if (folder?.isArchived) return;
  if (folder.redirectUrl && isSubscribed) {
    window.location.href = folder.redirectUrl;
    return;
  }
  if (!folder?.hasFiles) return;
  selectedElement.value = subscriptionName;
  filesInSeries.value = (folder.files || []).filter((nth) => !nth.isArchived && !nth.isHidden).map((nth) => ({
    title: nth.fileName,
    type: nth.fileType,
    url: nth.fileUrl,
    id: nth.fileUuid,
  }));

  showDrawer.value = !showDrawer.value;
  showFiles.value = true;
  showMarketSupportForm.value = false;
  product.value = null;
};

const shortenFileName = (fileName) => {
  const length = 12;
  return fileName.length > length ? fileName.substring(0, length) + '...' : fileName;
};

const userEmail = computed(() => {
  const jwtToken = localStorage.getItem("access");
  if (jwtToken) {
    const decoded = jwtDecode(jwtToken);
    return decoded.email;
  }
});

const clearProp = (newValue) => {
  if (!newValue) {
    if (route.query?.element) {
      router.push({ name: ROUTES.COST_ANALYSIS.name });
    }
    elementArrayProp.value = [];
  }
};

const filesInSeries = ref([]);
const columns = computed(() => [
  {
    key: "name",
    title(column) {
      return h(TableHeader, { text: t("Files.fileHeader") });
    },
    render: (row) => h(TableCell, { text: row.title, url: row.url, type: row.type }),
  },
  {
    key: "action",
    title: null,
    render: (row) => h(TableCellDownload, {
      selectedElement,
      text: row.title,
      url: row.url,
      id: row.id,
      folderName: folderName,
    }),
  },
]);

const data = computed(() =>
    filesInSeries.value?.map((nth) => ({
      title: nth.title,
      type: nth.type,
      url: nth.url,
      id: nth.id,
    }))
);

const handleCustomEvent = (service) => {
  product.value = service.subscriptionName;
  showDrawer.value = true;
  showMarketSupportForm.value = true;
  showFiles.value = false;
};

// ── Embedded helpers ───────────────────────────────────
const getLatestFile = (service) => {
  if (!service.folders?.length) return null;
  let latestFile = null;
  let latestFolder = null;
  service.folders.forEach(folder => {
    if (folder.isArchived || folder.isHidden || !folder.hasFiles || !folder.files?.length) return;
    folder.files.forEach(file => {
      if (file.isArchived || file.isHidden) return;
      if (!latestFile || new Date(file.startDate) > new Date(latestFile.startDate)) {
        latestFile = file;
        latestFolder = folder;
      }
    });
  });
  return latestFile ? { file: latestFile, folder: latestFolder } : null;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-GB");
  } catch {
    return "";
  }
};

const openLatestFile = (latestFileResult, subscriptionName = null) => {
  if (!latestFileResult) return;
  const { file, folder } = latestFileResult;
  if (file.fileUrl) {
    window.open(file.fileUrl, '_blank');
    return;
  }
  viewFiles(folder, true,subscriptionName);
};

// ── Lifecycle ──────────────────────────────────────────
onBeforeMount(() => window.$loading.start());

onMounted(async () => {
  if (!USER_FILES.value?.files) await getUserFiles();

  FOLDERS.value = MARKET_SERVICES2.value
      ?.filter(r => r.isPublic === true && r.serviceType === "COST_SERVICE_FILE")
      .map(service => {
        const fileMatch = USER_FILES.value?.files.find(file => file.element === service.element);
        return { ...service, files: fileMatch ? fileMatch.files : [] };
      });

  const foldersWithFiles = FOLDERS.value?.filter(folder =>
      folder.files.length > 0 &&
      folder.element.toUpperCase().replaceAll(' ', '_') !== CRITICAL_MATERIALS_NAME
  ).sort((a, b) => a.name.localeCompare(b.name));

  const otherFolders = FOLDERS.value?.filter(folder =>
      folder.files.length === 0 &&
      folder.element.toUpperCase().replaceAll(' ', '_') !== CRITICAL_MATERIALS_NAME
  ).sort((a, b) => a.name.localeCompare(b.name));

  sortedFolders.value = [...foldersWithFiles, ...otherFolders].filter(Boolean);

  if (route.query.element) {
    const result = sortedFolders.value
        .filter(r => r.element === route.query?.element)
        .map(r => ({
          series: DATA_SERIES_TYPE,
          name: r.name,
          element: r.element,
          files: r.files
        }));
    await viewFiles(result[0],true, result[0]?.subscriptionName);
  }

  if (USER_FILES.value?.files) window.$loading.finish();
});
</script>

<style lang="scss" scoped>
// ── Design tokens ──────────────────────────────────────
$navy:       #00213b;
$navy-light: #00213b;
$text:       #213ce5;
$black-text: #000;
$text-muted: #718096;
$border:     #e4e9f0;
$bg-row:     #f8f9fa;
$radius:     0;

// ── Section header card ────────────────────────────────
.section-header-card {
  border: none;
  border-radius: $radius;
  background: #fff;

  .header-title {
    font-size: 1rem;
    font-weight: 700;
    color: #000;
  }
}
.card {
  box-shadow: none;
}

// ── Accordion list ─────────────────────────────────────
.accordion-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  padding-bottom: 16px;
}

// ── Service card ───────────────────────────────────────
.accordion-service-card {
  background: $bg-row;
  overflow: hidden;
  &:hover {
    box-shadow: 0 2px 12px rgba($navy, 0.08);
  }
  &--locked {
    opacity: 0.75;
    pointer-events: none;
    .service-name { color: $text-muted; }
  }
}

// ── Service header row ─────────────────────────────────
.service-header {
  display: flex;
  align-items: flex-start;
  gap: 0;
}
.service-image {
  width: 50px;
  flex-shrink: 0;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.service-header-embedded {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.5rem 0 0.5rem;
  gap: 0.5rem;
  min-width: 0;
  overflow: hidden;
}

.services-grid {
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.service-card {
  overflow: hidden;
  flex-shrink: 0;
  &--unsubscribed {
    opacity: 0.65;
  }
}

.cost-analysis-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  > div {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  &.is-embedded {
    padding: 0;
    .cost-analysis-content {
      padding: 0.5rem;
    }
    .services-grid {
      gap: 0;
    }
    .service-title {
      font-weight: 500;
      font-size: 0.875rem;
      font-family: "GilroyMedium", sans-serif;
    }
  }
}

.service-info {
  align-self: flex-start;
  flex: 1;
  min-width: 0;
  width: 0;
}

.service-title-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.35rem;
}

.service-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #000;
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.service-actions {
  display: flex;
  flex-direction: column;
  padding: 0 1px 0 0;
  gap: 0.25rem;
  min-width: 0;
  overflow: hidden;
}

.latest-file-link {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  width: 100%;

  .latest-file-name {
    font-size: 0.75rem;
    font-weight: 500;
    color: #000;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .latest-file-date {
    font-size: 0.6875rem;
    font-style: italic;
    color: #718096;
    font-weight: 500;
    white-space: nowrap;
  }

  &:hover .latest-file-name {
    font-weight: 600;
    color: #2184ff;
    text-decoration: underline;
  }
}

.no-files-label {
  font-size: 0.75rem;
  color: #718096;
  font-style: italic;
}

.service-thumbnail {
  position: relative;
  align-self: flex-start;
  flex: 0 0 143px;
  min-height: 65px;
  max-height: 143px;

  .thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .thumb-lock-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba($navy, 0.45);
    color: #fff;
    font-size: 1.2rem;
  }
}

.service-meta {
  flex: 1;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.service-identity {
  display: block;

  .service-name {
    display: block;
    font-size: 0.8125rem;
    font-weight: 400;
    letter-spacing: 0.125rem;
    text-transform: uppercase;
    color: $text-muted;
    margin-bottom: 4px;
    padding-bottom: 3px;
    border-bottom: 1px solid $border;
  }
}

// ── Folder column groups ───────────────────────────────
.folder-columns {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0 24px;
}

// ── Individual folder row ──────────────────────────────
.folder-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.2rem 0;
  margin-bottom: 2px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  gap: 8px;

  &:hover:not(.folder-row--disabled) {
    .folder-name,.updated-date {
      color: $text;
      text-decoration: underline;
    }
  }

  &--active {
    background: $bg-row !important;
    .folder-name,
    .updated-date {
      color: $text !important;
      text-decoration: underline;
    }
  }

  &--disabled {
    cursor: default;
    opacity: 0.55;
  }

  .folder-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #000;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 1;
  }

  .folder-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .updated-date {
    font-size: 0.68rem;
    color: $text-muted;
    white-space: nowrap;
  }

  .chevron-icon {
    font-size: 0.7rem;
    color: $text-muted;
    transition: transform 0.2s;
  }

  .folder-badge--link {
    font-size: 0.7rem;
    color: $navy;
  }
}

// ── Expanded file panel ────────────────────────────────
.file-list-panel {
  background: $bg-row;
}

.file-list-inner {
  padding: 6px 14px 10px;
}

.file-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;

  & + .file-row {
    border-top: 1px solid $border;
  }

  &:hover {
    background: rgba($navy, 0.03);
  }

  .file-info {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1 1 0;
    min-width: 0;

    .file-type-icon {
      font-size: 1rem;
      color: $navy;
      flex-shrink: 0;
    }

    .file-title {
      font-size: 0.8125rem;
      font-weight: 500;
      color: $black-text;
      word-break: break-word;
      white-space: normal;
    }
  }

  .file-too-large-note {
    display: block;
    font-size: 0.7rem;
    font-style: italic;
    color: $text-muted;
  }

  .file-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    margin-left: auto;
    align-self: flex-start;
  }
}

.inline-file-inner {
  display: grid;
}

// ── Action buttons ────────────────────────────────────
.action-btn {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  font-size: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: filter 0.15s, transform 0.1s;
  flex-shrink: 0;
  border: 1px solid transparent;

  &:active { transform: scale(0.97); }

  i { font-size: 0.5rem; }
  &:hover {
    border: 1px solid #00aeef !important;
  }
  &--view { background: linear-gradient(90deg, #213ce5, #2380f2); color: #fff; &:hover { filter: brightness(1.08); color: #fff; } }
  &--email { background: linear-gradient(90deg, #213ce5, #2380f2); color: #fff; &:hover { filter: brightness(1.08); } &:disabled { background: transparent; pointer-events: none; i { color: #213ce5; font-size: 0.75rem; }}}
  &--download { background: linear-gradient(90deg, #213ce5, #2380f2); color: #fff; &:hover { filter: brightness(1.08); } }
}

.no-files {
  font-size: 0.8rem;
  color: $text-muted;
  padding: 10px 4px;
}

// ── Dates hidden (kept in DOM, just not shown) ─────────
.updated-date,
.latest-file-date {
  display: none !important;
}

// ── Responsive ────────────────────────────────────────
@media (max-width: 1400px) {
  .updated-date {
    display: none;
  }
}
@media (max-width: 768px) {
  .accordion-list {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 640px) {
  .service-thumbnail { display: none; }
  .updated-date { display: none; }
  .folder-columns { gap: 12px; }
  .action-btn span { display: none; }
  .action-btn { padding: 5px 8px; }
}
// ── Loading state ──────────────────────────────────────
.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  width: 100%;
  gap: 1.5rem;
}

.custom-loader {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.loader-circle {
  width: 12px;
  height: 12px;
  background-color: $navy;
  border-radius: 50%;
  animation: loader-bounce 1.4s ease-in-out infinite both;

  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }
  &:nth-child(3) { animation-delay: 0s; }
  &:nth-child(4) { animation-delay: 0.16s; }
}

@keyframes loader-bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.3; }
  40%           { transform: scale(1); opacity: 1;   }
}

.loading-text {
  color: $navy;
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  opacity: 0.8;
}
</style>